import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";

const HEADERS = [
  "Payment Method Type",
  "Payment Method Name",
  "Details",
  "Note",
  "Is Enabled",
  "Created At",
  "Actions",
];

const EnabledBadge = ({ enabled }) => (
  <span
    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {enabled ? "Yes" : "No"}
  </span>
);

const PaymentMethodList = ({ methods, onEdit, onDelete, readOnly = false }) => {
  const renderBankInfo = (method) => {
    if (method.payment_method !== "bank_transfer") {
      return <span className="text-gray-400">—</span>;
    }

    const bankDetails = method.bank_transfer_detail || method.details || {};

    const fields = [
      { label: "Bank Name", value: bankDetails.bank_name },
      { label: "Bank Address", value: bankDetails.bank_address },
      { label: "SWIFT", value: bankDetails.swift },
      { label: "Beneficiary Name", value: bankDetails.beneficiary_name },
      { label: "Beneficiary Address", value: bankDetails.beneficiary_address },
      { label: "Account Number", value: bankDetails.account_number },
      { label: "IFSC Code", value: bankDetails.ifsc_code },
      { label: "IBAN", value: bankDetails.iban },
      { label: "Sort Code", value: bankDetails.sort_code },
      { label: "Country", value: bankDetails.country },
      { label: "State/Province/Region", value: bankDetails.state_region },
      { label: "City", value: bankDetails.city },
      { label: "Postal/ZIP Code", value: bankDetails.postal_code },
    ];

    const filtered = fields.filter((f) => f.value && String(f.value).trim() !== "");

    return filtered.length > 0 ? (
      <div className="text-xs text-gray-700 space-y-0.5 max-h-32 overflow-y-auto pr-1 text-left">
        {filtered.map((f, i) => (
          <div key={i}>
            <span className="font-medium text-gray-500">{f.label}:</span> {f.value}
          </div>
        ))}
      </div>
    ) : (
      <span className="text-gray-400">—</span>
    );
  };

  const renderPaymentMethodName = (method) => {
    const type = method.payment_method;
    if (type === "bank_transfer")
      return method.bank_transfer_detail?.payment_method_name || "—";
    if (["paypal", "payoneer", "skrill"].includes(type))
      return (
        method[`${type}_detail`]?.email ||
        method.email_payment_detail?.email ||
        "—"
      );
    if (type === "other")
      return method.other_payment_detail?.payment_method_name || "—";
    return "—";
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return dayjs(d).format("YYYY-MM-DD HH:mm");
  };

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1100px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {HEADERS.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 ? (
              <tr>
                <td
                  colSpan={HEADERS.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  No payment methods added yet.
                </td>
              </tr>
            ) : (
              methods.map((method, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                return (
                  <tr
                    key={method.id}
                    className={`${rowBg} hover:bg-brand-50 transition-colors`}
                  >
                    <td className="px-3 py-2 text-xs text-center capitalize">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-900">
                          {method.payment_method.replace("_", " ")}
                        </span>
                        {method.is_default && (
                          <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">
                            Default
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-xs text-center text-gray-900">
                      {renderPaymentMethodName(method)}
                    </td>

                    <td className="px-3 py-2 text-xs">
                      {renderBankInfo(method)}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-700 text-left">
                      <div className="whitespace-pre-wrap max-h-32 overflow-y-auto pr-1">
                        {method.note || "—"}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-xs text-center">
                      <EnabledBadge enabled={method.active_flag} />
                    </td>

                    <td className="px-3 py-2 text-xs text-center text-gray-500">
                      {formatDate(method.createdAt)}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {readOnly ? (
                        <span className="text-[10px] text-gray-400">—</span>
                      ) : (
                        <div className="flex justify-center gap-3">
                          <Pencil
                            className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                            title="Edit"
                            onClick={() => onEdit?.(method)}
                          />
                          <Trash2
                            className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                            title="Delete"
                            onClick={() => onDelete?.(method.id)}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentMethodList;
